import { CabDropdownProps, CabDropdown } from "@CabComponents/CabDropdown";
import { FormControl, InputLabel } from "@mui/material";
import { GridFilterInputMultipleValueProps } from "@mui/x-data-grid-pro";
import { AbstractEventAnalyticsLabel } from "../../store";
import { ReactElement, useEffect } from "react";
import colors from "../../colors";
import { intersection as lodashIntersection } from "lodash-es";

export type FilterSelectProps<T extends AbstractEventAnalyticsLabel = AbstractEventAnalyticsLabel> =
  GridFilterInputMultipleValueProps & CabDropdownProps<string | number> & {
    rawOptions: {[key: string]: T};
  };

function FilterSelect<T extends AbstractEventAnalyticsLabel>(props: FilterSelectProps<T>) {
  const {options, rawOptions, applyValue, multiple, item} = {
    ...props
  };

  const retypedOptions = options as unknown as { 
    value: string | number;
    label: string | ReactElement;
    disabled?: boolean | undefined;
    tooltipText?: string | undefined;
  }[];

  useEffect(() => {
    if (Array.isArray(item.value)) {
      const intersection = lodashIntersection(
        Object.keys(rawOptions).map((key: string) => Number(key)),
        item.value
      );
      if (intersection.length === 0 ) {
        applyValue({...item, value: []});
      }
    } else {
      if (!rawOptions[item.value]) {
        applyValue({...item, value: []});
      }
    }
  // We need to reset selects to ensure bad state is not retains when rawOptions change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rawOptions)]);

  const optionsMap: {[key: number | string]: {value: string | number, label: string | React.ReactElement}} = {};
  retypedOptions.forEach(opt  => {
    optionsMap[opt.value] = opt;
  });

  const labelId = `filter-select-${item.id ?? item.field}-label`;
  const inputId = `filter-select-${item.id ?? item.field}`;

  return <FormControl fullWidth size="small" variant="outlined">
    <InputLabel id={labelId} shrink>Value</InputLabel>
    <CabDropdown
      label="Value"
      inputId={inputId}
      size="small"
      multiple={multiple}
      options={options}
      value={item.value}
      onChange={(e) => applyValue({...item, value: e.target.value})}
      overrides={{
        renderValue: (value: unknown) => {
          const ids = value as number[];
          const optionObjects = ids.map(eventId => eventId != null && rawOptions[eventId]
            ? rawOptions[eventId] 
            : {id: -1, name: "None", color: colors.white800} as T);
          return <>{optionObjects.map(obj => retypedOptions.find(o => o.value === obj.id)?.label)}</>;
        },
        sx: {
          width: "100%",
          "& .MuiSelect-multiple": {
            paddingLeft: 0.5
          },
          "& .MuiSvgIcon-root": {
            height: 16,
            width: 16
          },
          height: 38
        },
        labelId,
        notched: true
      }}
    />
  </FormControl>;
}

export default FilterSelect;
